import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Save, Trees, Coffee, Droplets, Bus, Link2, Trash2, ArrowLeft, Toilet, Car, ShoppingBag, Beer, Utensils, Flame, ShowerHead } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const LEAD_OPTIONS = [
  { v: "strictly_on_lead", label: "Strictly on lead" },
  { v: "off_lead_allowed", label: "Off-lead allowed" },
  { v: "fully_enclosed", label: "Fully enclosed" },
];
const ENERGY_OPTIONS = [
  { v: "chill", label: "Chill" },
  { v: "high_energy", label: "High energy" },
];
// Same amenity list as the Add form — kept in sync so a create + edit round-
// trip preserves every field the schema knows about.
const AMENITY_FIELDS = [
  { k: "has_water_tap", label: "Water tap", Icon: Droplets },
  { k: "dog_waste_bins", label: "Dog waste bins", Icon: Trees },
  { k: "cafe_on_site", label: "Cafe on site", Icon: Coffee },
  { k: "has_toilets", label: "Toilets", Icon: Toilet },
  { k: "has_car_park", label: "Car park", Icon: Car },
  { k: "has_shop", label: "Shop", Icon: ShoppingBag },
  { k: "pub_nearby", label: "Pub nearby", Icon: Beer },
  { k: "picnic_tables", label: "Picnic tables", Icon: Utensils },
  { k: "bbq_allowed", label: "BBQ area", Icon: Flame },
  { k: "dog_wash_station", label: "Dog wash", Icon: ShowerHead },
];

const EMPTY = {
  name: "",
  description: "",
  lat: "",
  lng: "",
  has_water_tap: false,
  dog_waste_bins: false,
  cafe_on_site: false,
  has_toilets: false,
  has_car_park: false,
  has_shop: false,
  pub_nearby: false,
  picnic_tables: false,
  bbq_allowed: false,
  dog_wash_station: false,
  lead_status: "strictly_on_lead",
  energy_match: "chill",
  bus_distance_meters: "",
  bus_path_quality: "",
  linked_trail_ids: "",
};

export default function AdminEditBasecamp() {
  const { user, loading } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const bc = useQuery({
    queryKey: ["admin-basecamp", id],
    enabled: !!id && !!user?.is_admin,
    queryFn: async () => (await api.get(`/basecamps/${id}`)).data,
  });

  // Hydrate form once the basecamp is fetched. We stringify numeric fields so
  // controlled `<input>` values stay stable while the user types.
  useEffect(() => {
    const d = bc.data;
    if (!d) return;
    setForm({
      name: d.name || "",
      description: d.description || "",
      lat: d.lat != null ? String(d.lat) : "",
      lng: d.lng != null ? String(d.lng) : "",
      has_water_tap: !!d.has_water_tap,
      dog_waste_bins: !!d.dog_waste_bins,
      cafe_on_site: !!d.cafe_on_site,
      has_toilets: !!d.has_toilets,
      has_car_park: !!d.has_car_park,
      has_shop: !!d.has_shop,
      pub_nearby: !!d.pub_nearby,
      picnic_tables: !!d.picnic_tables,
      bbq_allowed: !!d.bbq_allowed,
      dog_wash_station: !!d.dog_wash_station,
      lead_status: d.lead_status || "strictly_on_lead",
      energy_match: d.energy_match || "chill",
      bus_distance_meters: d.bus_distance_meters != null ? String(d.bus_distance_meters) : "",
      bus_path_quality: d.bus_path_quality || "",
      linked_trail_ids: (d.linked_trail_ids || []).join(", "),
    });
  }, [bc.data]);

  if (loading) return <div className="p-8 text-brand-subtle">Loading…</div>;
  if (!user?.is_admin) return <Navigate to="/" replace />;

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required.");
    if (!form.lat || !form.lng) return toast.error("Lat and lng are required.");

    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      lat: parseFloat(form.lat),
      lng: parseFloat(form.lng),
      has_water_tap: form.has_water_tap,
      dog_waste_bins: form.dog_waste_bins,
      cafe_on_site: form.cafe_on_site,
      has_toilets: form.has_toilets,
      has_car_park: form.has_car_park,
      has_shop: form.has_shop,
      pub_nearby: form.pub_nearby,
      picnic_tables: form.picnic_tables,
      bbq_allowed: form.bbq_allowed,
      dog_wash_station: form.dog_wash_station,
      lead_status: form.lead_status,
      energy_match: form.energy_match,
      bus_distance_meters: form.bus_distance_meters ? parseInt(form.bus_distance_meters, 10) : null,
      bus_path_quality: form.bus_path_quality.trim(),
      linked_trail_ids: form.linked_trail_ids.split(",").map((s) => s.trim()).filter(Boolean),
    };

    setSaving(true);
    try {
      await api.patch(`/admin/basecamps/${id}`, payload);
      toast.success("Basecamp updated");
      navigate("/admin/locations");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save changes.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete basecamp "${form.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/basecamps/${id}`);
      toast.success("Basecamp deleted");
      navigate("/admin/locations");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete.");
      setDeleting(false);
    }
  };

  if (bc.isLoading) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 text-brand-subtle flex items-center gap-2">
        <Loader2 size={16} className="animate-spin" /> Loading basecamp…
      </div>
    );
  }
  if (bc.isError) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6" data-testid="admin-edit-basecamp-error">
        <p className="text-brand-error">Couldn&apos;t load this basecamp.</p>
        <Link to="/admin/locations" className="text-sm text-brand-subtle hover:underline">Back to admin</Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6" data-testid="admin-edit-basecamp">
      <header className="mb-5">
        <Link to="/admin/locations" className="text-xs text-brand-subtle hover:underline inline-flex items-center gap-1">
          <ArrowLeft size={11} /> All locations
        </Link>
        <div className="td-section-label mt-1">Admin</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-brand-text mt-1 flex items-center gap-2">
          <Trees size={28} className="text-brand-primary" /> Edit basecamp
        </h1>
        <p className="text-brand-subtle text-sm mt-1 truncate">{id}</p>
      </header>

      <form onSubmit={submit} className="td-card p-5 sm:p-6 space-y-4">
        <div>
          <label className="td-section-label">Name</label>
          <input
            data-testid="bc-edit-name"
            className="td-input mt-1"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            required
          />
        </div>

        <div>
          <label className="td-section-label">Description</label>
          <textarea
            data-testid="bc-edit-description"
            rows={3}
            className="td-input mt-1 resize-none"
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="td-section-label">Lat</label>
            <input
              data-testid="bc-edit-lat"
              type="number" step="any"
              className="td-input mt-1"
              value={form.lat}
              onChange={(e) => set("lat", e.target.value)}
              required
            />
          </div>
          <div>
            <label className="td-section-label">Lng</label>
            <input
              data-testid="bc-edit-lng"
              type="number" step="any"
              className="td-input mt-1"
              value={form.lng}
              onChange={(e) => set("lng", e.target.value)}
              required
            />
          </div>
        </div>

        <fieldset className="pt-2">
          <legend className="td-section-label">Amenities</legend>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
            {AMENITY_FIELDS.map(({ k, label, Icon }) => (
              <label
                key={k}
                className={`flex items-center gap-2 p-2.5 rounded-xl border cursor-pointer transition-colors ${
                  form[k] ? "bg-brand-primary/10 border-brand-primary/40" : "bg-brand-sand/50 border-brand-border"
                }`}
              >
                <input
                  type="checkbox"
                  data-testid={`bc-edit-${k}`}
                  checked={form[k]}
                  onChange={(e) => set(k, e.target.checked)}
                  className="accent-brand-primary"
                />
                <Icon size={13} className="text-brand-primary" />
                <span className="text-sm">{label}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <fieldset>
            <legend className="td-section-label">Lead status</legend>
            <select
              data-testid="bc-edit-lead-status"
              className="td-input mt-1"
              value={form.lead_status}
              onChange={(e) => set("lead_status", e.target.value)}
            >
              {LEAD_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </fieldset>
          <fieldset>
            <legend className="td-section-label">Energy match</legend>
            <select
              data-testid="bc-edit-energy"
              className="td-input mt-1"
              value={form.energy_match}
              onChange={(e) => set("energy_match", e.target.value)}
            >
              {ENERGY_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </fieldset>
        </div>

        <fieldset className="pt-2">
          <legend className="td-section-label flex items-center gap-1"><Bus size={11} /> Transit last-mile</legend>
          <div className="grid grid-cols-2 gap-3 mt-1">
            <input
              data-testid="bc-edit-bus-dist"
              type="number" min="0" step="1"
              className="td-input"
              value={form.bus_distance_meters}
              onChange={(e) => set("bus_distance_meters", e.target.value)}
              placeholder="Bus stop distance (m)"
            />
            <input
              data-testid="bc-edit-bus-path"
              className="td-input"
              value={form.bus_path_quality}
              onChange={(e) => set("bus_path_quality", e.target.value)}
              placeholder="Path quality e.g. Paved path"
            />
          </div>
        </fieldset>

        <div>
          <label className="td-section-label flex items-center gap-1"><Link2 size={11} /> Linked trail IDs</label>
          <input
            data-testid="bc-edit-linked"
            className="td-input mt-1"
            value={form.linked_trail_ids}
            onChange={(e) => set("linked_trail_ids", e.target.value)}
            placeholder="Comma-separated location_id values"
          />
        </div>

        <div className="pt-2 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <button
              type="submit"
              data-testid="bc-edit-submit"
              disabled={saving}
              className="td-btn-primary !py-2.5 !px-5"
            >
              {saving ? (<><Loader2 size={14} className="animate-spin" /> Saving…</>) : (<><Save size={14} /> Save changes</>)}
            </button>
            <Link to="/admin/locations" className="text-sm text-brand-subtle hover:underline">Cancel</Link>
          </div>
          <button
            type="button"
            data-testid="bc-edit-delete"
            disabled={deleting}
            onClick={remove}
            className="td-btn-secondary !py-2 !px-3 !text-xs text-brand-error border-brand-error/30 hover:bg-brand-error/10"
          >
            {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Delete
          </button>
        </div>
      </form>
    </div>
  );
}
